import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { 
  User, 
  Bell, 
  Monitor,
  BookOpen,
  Globe,
  Mail,
  Building2,
  Shield,
  Battery,
  Wrench,
  Activity,
  BarChart3,
  Map,
  Package,
  Ticket,
  Check,
  Zap,
  RefreshCw,
  LayoutGrid,
  Eye
} from 'lucide-react';
import { Switch } from '../components/ui/switch';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import './Settings.css';

const LANGUAGES = [
  { code: 'en', label: 'English', flag: '🇬🇧' },
  { code: 'fr', label: 'Français', flag: '🇫🇷' },
  { code: 'de', label: 'Deutsch', flag: '🇩🇪' },
  { code: 'lv', label: 'Latviešu', flag: '🇱🇻' },
  { code: 'lt', label: 'Lietuvių', flag: '🇱🇹' }
];

const Settings = () => {
  const { t, i18n } = useTranslation();
  const { user, profile } = useAuth();
  const [activeTab, setActiveTab] = useState('profile');
  
  const [settings, setSettings] = useState({
    notifications: {
      lowBattery: true,
      maintenance: true,
      statusChange: false,
      dailyReport: true
    },
    display: {
      compactMode: false,
      showBatteryPercentage: true,
      autoRefresh: true
    }
  });

  const handleLanguageChange = (langCode) => {
    i18n.changeLanguage(langCode);
    localStorage.setItem('i18nextLng', langCode);
  };

  const currentLang = LANGUAGES.find(l => l.code === i18n.language) || LANGUAGES[0];

  const getUserInitials = () => {
    const first = profile?.first_name?.[0] || user?.email?.[0] || 'U';
    const last = profile?.last_name?.[0] || '';
    return (first + last).toUpperCase();
  };

  const tabs = [
    { id: 'profile', label: t('settings.profile'), icon: User },
    { id: 'notifications', label: t('settings.notifications'), icon: Bell },
    { id: 'display', label: t('settings.display'), icon: Monitor },
    { id: 'guide', label: t('settings.userGuide'), icon: BookOpen }
  ];

  return (
    <div className="settings-page-v2">
      {/* Header */}
      <div className="settings-header-v2">
        <h1>{t('settings.title')}</h1>
        <p>{t('settings.subtitle')}</p>
      </div>

      {/* Tab Navigation */}
      <div className="settings-tabs-v2" data-testid="settings-tabs">
        {tabs.map(tab => {
          const Icon = tab.icon;
          return (
            <button
              key={tab.id}
              className={`settings-tab-btn ${activeTab === tab.id ? 'active' : ''}`}
              onClick={() => setActiveTab(tab.id)}
              data-testid={`tab-${tab.id}`}
            >
              <Icon size={18} />
              <span>{tab.label}</span>
            </button>
          );
        })}
      </div>

      {/* Tab Content */}
      <div className="settings-content-v2">
        {/* Profile Tab */}
        {activeTab === 'profile' && (
          <div className="settings-panel">
            <div className="settings-cards-grid">
              {/* Profile Card */}
              <div className="settings-card-v2">
                <div className="card-header-v2">
                  <User size={20} className="card-icon" />
                  <h3>{t('settings.accountInfo')}</h3>
                </div>
                <div className="card-body-v2">
                  <div className="profile-avatar-box">
                    <div className="avatar-circle" data-testid="profile-avatar">
                      {getUserInitials()}
                    </div>
                    <div className="avatar-info">
                      <h4>{profile?.first_name || '-'} {profile?.last_name || ''}</h4>
                      <span className="role-tag">{profile?.role || 'viewer'}</span>
                    </div>
                  </div>
                  
                  <div className="info-list">
                    <div className="info-row">
                      <Mail size={16} />
                      <div>
                        <label>{t('auth.email')}</label>
                        <span data-testid="profile-email">{user?.email || 'N/A'}</span>
                      </div>
                    </div>
                    <div className="info-row">
                      <Building2 size={16} />
                      <div>
                        <label>{t('auth.organization')}</label>
                        <span data-testid="profile-org">{profile?.organization || '-'}</span>
                      </div>
                    </div>
                    <div className="info-row">
                      <Shield size={16} />
                      <div>
                        <label>{t('settings.role')}</label>
                        <span>{profile?.role || 'viewer'}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Language Card */}
              <div className="settings-card-v2">
                <div className="card-header-v2">
                  <Globe size={20} className="card-icon" />
                  <h3>{t('settings.language')}</h3>
                </div>
                <div className="card-body-v2">
                  <p className="card-desc">{t('settings.languageDesc')}</p>
                  <Select value={i18n.language} onValueChange={handleLanguageChange}>
                    <SelectTrigger className="lang-select" data-testid="language-selector">
                      <SelectValue>
                        <span className="lang-option">
                          <span className="lang-flag">{currentLang.flag}</span>
                          {currentLang.label}
                        </span>
                      </SelectValue>
                    </SelectTrigger>
                    <SelectContent>
                      {LANGUAGES.map(lang => (
                        <SelectItem key={lang.code} value={lang.code}>
                          <span className="lang-option">
                            <span className="lang-flag">{lang.flag}</span>
                            {lang.label}
                          </span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Notifications Tab */}
        {activeTab === 'notifications' && (
          <div className="settings-panel">
            <div className="settings-card-v2 wide">
              <div className="card-header-v2">
                <Bell size={20} className="card-icon" />
                <h3>{t('settings.alertPreferences')}</h3>
              </div>
              <div className="card-body-v2">
                <div className="toggle-list">
                  <div className="toggle-item" data-testid="setting-low-battery">
                    <div className="toggle-icon warning"><Battery size={20} /></div>
                    <div className="toggle-info">
                      <Label>{t('settings.lowBatteryAlerts')}</Label>
                      <p>{t('settings.lowBatteryDesc')}</p>
                    </div>
                    <Switch 
                      checked={settings.notifications.lowBattery}
                      onCheckedChange={(checked) => setSettings(s => ({
                        ...s, notifications: {...s.notifications, lowBattery: checked}
                      }))}
                    />
                  </div>

                  <div className="toggle-item" data-testid="setting-maintenance">
                    <div className="toggle-icon"><Wrench size={20} /></div>
                    <div className="toggle-info">
                      <Label>{t('settings.maintenanceAlerts')}</Label>
                      <p>{t('settings.maintenanceDesc')}</p>
                    </div>
                    <Switch 
                      checked={settings.notifications.maintenance}
                      onCheckedChange={(checked) => setSettings(s => ({
                        ...s, notifications: {...s.notifications, maintenance: checked}
                      }))}
                    />
                  </div>

                  <div className="toggle-item" data-testid="setting-status-change">
                    <div className="toggle-icon"><Activity size={20} /></div>
                    <div className="toggle-info">
                      <Label>{t('settings.statusChangeAlerts')}</Label>
                      <p>{t('settings.statusChangeDesc')}</p>
                    </div>
                    <Switch 
                      checked={settings.notifications.statusChange}
                      onCheckedChange={(checked) => setSettings(s => ({
                        ...s, notifications: {...s.notifications, statusChange: checked}
                      }))}
                    />
                  </div>

                  <div className="toggle-item" data-testid="setting-daily-report">
                    <div className="toggle-icon"><BarChart3 size={20} /></div>
                    <div className="toggle-info">
                      <Label>{t('settings.dailyReport')}</Label>
                      <p>{t('settings.dailyReportDesc')}</p>
                    </div>
                    <Switch 
                      checked={settings.notifications.dailyReport}
                      onCheckedChange={(checked) => setSettings(s => ({
                        ...s, notifications: {...s.notifications, dailyReport: checked}
                      }))}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Display Tab */}
        {activeTab === 'display' && (
          <div className="settings-panel">
            <div className="settings-card-v2 wide">
              <div className="card-header-v2">
                <Monitor size={20} className="card-icon" />
                <h3>{t('settings.appearance')}</h3>
              </div>
              <div className="card-body-v2">
                <div className="toggle-list">
                  <div className="toggle-item" data-testid="setting-compact-mode">
                    <div className="toggle-icon"><LayoutGrid size={20} /></div>
                    <div className="toggle-info">
                      <Label>{t('settings.compactMode')}</Label>
                      <p>{t('settings.compactModeDesc')}</p>
                    </div>
                    <Switch 
                      checked={settings.display.compactMode}
                      onCheckedChange={(checked) => setSettings(s => ({
                        ...s, display: {...s.display, compactMode: checked}
                      }))}
                    />
                  </div>

                  <div className="toggle-item" data-testid="setting-show-battery">
                    <div className="toggle-icon"><Eye size={20} /></div>
                    <div className="toggle-info">
                      <Label>{t('settings.showBattery')}</Label>
                      <p>{t('settings.showBatteryDesc')}</p>
                    </div>
                    <Switch 
                      checked={settings.display.showBatteryPercentage}
                      onCheckedChange={(checked) => setSettings(s => ({
                        ...s, display: {...s.display, showBatteryPercentage: checked}
                      }))}
                    />
                  </div>

                  <div className="toggle-item" data-testid="setting-auto-refresh">
                    <div className="toggle-icon"><RefreshCw size={20} /></div>
                    <div className="toggle-info">
                      <Label>{t('settings.autoRefresh')}</Label>
                      <p>{t('settings.autoRefreshDesc')}</p>
                    </div>
                    <Switch 
                      checked={settings.display.autoRefresh}
                      onCheckedChange={(checked) => setSettings(s => ({
                        ...s, display: {...s.display, autoRefresh: checked}
                      }))}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* User Guide Tab */}
        {activeTab === 'guide' && (
          <div className="settings-panel guide-panel">
            {/* Welcome Banner */}
            <div className="guide-banner" data-testid="guide-welcome">
              <h2>{t('guide.welcomeTitle')}</h2>
              <p>{t('guide.welcomeDesc')}</p>
            </div>

            {/* Feature Cards */}
            <div className="guide-grid">
              {/* Dashboard */}
              <div className="guide-card" data-testid="guide-dashboard">
                <div className="guide-card-header">
                  <div className="guide-icon blue"><BarChart3 size={22} /></div>
                  <h4>{t('guide.dashboardTitle')}</h4>
                </div>
                <p className="guide-desc">{t('guide.dashboardDesc')}</p>
                <ul className="guide-features">
                  <li><Check size={14} /> {t('guide.dashboardFeature1')}</li>
                  <li><Check size={14} /> {t('guide.dashboardFeature2')}</li>
                  <li><Check size={14} /> {t('guide.dashboardFeature3')}</li>
                  <li><Check size={14} /> {t('guide.dashboardFeature4')}</li>
                </ul>
              </div>

              {/* Map */}
              <div className="guide-card" data-testid="guide-map">
                <div className="guide-card-header">
                  <div className="guide-icon green"><Map size={22} /></div>
                  <h4>{t('guide.mapTitle')}</h4>
                </div>
                <p className="guide-desc">{t('guide.mapDesc')}</p>
                <ul className="guide-features">
                  <li><Check size={14} /> {t('guide.mapFeature1')}</li>
                  <li><Check size={14} /> {t('guide.mapFeature2')}</li>
                  <li><Check size={14} /> {t('guide.mapFeature3')}</li>
                  <li><Check size={14} /> {t('guide.mapFeature4')}</li>
                </ul>
              </div>

              {/* Assets */}
              <div className="guide-card" data-testid="guide-assets">
                <div className="guide-card-header">
                  <div className="guide-icon red"><Package size={22} /></div>
                  <h4>{t('guide.assetsTitle')}</h4>
                </div>
                <p className="guide-desc">{t('guide.assetsDesc')}</p>
                <ul className="guide-features">
                  <li><Check size={14} /> {t('guide.assetsFeature1')}</li>
                  <li><Check size={14} /> {t('guide.assetsFeature2')}</li>
                  <li><Check size={14} /> {t('guide.assetsFeature3')}</li>
                  <li><Check size={14} /> {t('guide.assetsFeature4')}</li>
                </ul>
                <div className="status-legend-box">
                  <h5>{t('guide.statusLegend')}</h5>
                  <div className="status-grid">
                    <span><i className="dot ready"></i> {t('status.ready')}</span>
                    <span><i className="dot inuse"></i> {t('status.inuse')}</span>
                    <span><i className="dot toclean"></i> {t('status.toclean')}</span>
                    <span><i className="dot outoforder"></i> {t('status.outoforder')}</span>
                  </div>
                </div>
              </div>

              {/* Tickets */}
              <div className="guide-card" data-testid="guide-tickets">
                <div className="guide-card-header">
                  <div className="guide-icon orange"><Ticket size={22} /></div>
                  <h4>{t('guide.ticketsTitle')}</h4>
                </div>
                <p className="guide-desc">{t('guide.ticketsDesc')}</p>
                <ul className="guide-features">
                  <li><Check size={14} /> {t('guide.ticketsFeature1')}</li>
                  <li><Check size={14} /> {t('guide.ticketsFeature2')}</li>
                  <li><Check size={14} /> {t('guide.ticketsFeature3')}</li>
                </ul>
              </div>

              {/* Analytics */}
              <div className="guide-card" data-testid="guide-analytics">
                <div className="guide-card-header">
                  <div className="guide-icon purple"><Zap size={22} /></div>
                  <h4>{t('guide.analyticsTitle')}</h4>
                </div>
                <p className="guide-desc">{t('guide.analyticsDesc')}</p>
                <ul className="guide-features">
                  <li><Check size={14} /> {t('guide.analyticsFeature1')}</li>
                  <li><Check size={14} /> {t('guide.analyticsFeature2')}</li>
                  <li><Check size={14} /> {t('guide.analyticsFeature3')}</li>
                </ul>
              </div>

              {/* Tips */}
              <div className="guide-card tips-card" data-testid="guide-tips">
                <div className="guide-card-header">
                  <div className="guide-icon pink"><BookOpen size={22} /></div>
                  <h4>{t('guide.tipsTitle')}</h4>
                </div>
                <div className="tips-list">
                  <div className="tip"><span>1</span><p>{t('guide.tip1')}</p></div>
                  <div className="tip"><span>2</span><p>{t('guide.tip2')}</p></div>
                  <div className="tip"><span>3</span><p>{t('guide.tip3')}</p></div>
                  <div className="tip"><span>4</span><p>{t('guide.tip4')}</p></div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Settings;
